package com.klantenstop.web.pages.legal

import androidx.compose.runtime.Composable
import com.klantenstop.web.components.H1Heading
import com.klantenstop.web.components.H2Heading
import com.klantenstop.web.components.MarkDownSpanText
import com.klantenstop.web.components.layout.PageLayout
import com.klantenstop.web.components.layout.PageSeoConfig
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.styles.Typography
import com.materialkobweb.styles.MaterialColorVars
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.px

@Page("/privacybeleid")
@Composable
fun PrivacyPolicyPage() {
    val breakpoint = rememberBreakpoint()

    PageLayout(
        title = "Privacybeleid en gegevensbescherming",
        seo = PageSeoConfig.Managed(
            description = "Het privacybeleid van KlantenStop: welke gegevens we verzamelen bij zoekopdrachten en bedrijfsregistraties, hoe we ze gebruiken en beveiligen, en wat uw rechten zijn.",
            indexable = true,
            breadcrumbLabel = "Privacybeleid",
        )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    topBottom = if (breakpoint >= Breakpoint.MD) 64.px else 32.px,
                    leftRight = if (breakpoint >= Breakpoint.MD) 48.px else 20.px
                ),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(40.px)
        ) {
            // Header
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(900.px),
                verticalArrangement = Arrangement.spacedBy(12.px)
            ) {
                H1Heading(
                    modifier = Modifier.fillMaxWidth(),
                    text = "Privacybeleid",
                    color = MaterialColorVars.Primary.value()
                )
                SpanText(
                    modifier = Modifier
                        .fontSize(14.px)
                        .color(MaterialColorVars.OnSurfaceVariant.value()),
                    text = "Laatste update: 12 mei 2026"
                )
                SpanText(
                    modifier = Modifier
                        .fillMaxWidth()
                        .fontSize(if (breakpoint >= Breakpoint.MD) 16.px else 14.px)
                        .lineHeight(if (breakpoint >= Breakpoint.MD) 26.px else 22.px)
                        .color(MaterialColorVars.OnSurface.value()),
                    text = "C.B. Connect I.T. B.V. (\"wij\", \"ons\" of het \"Bedrijf\") verbindt zich ertoe uw privacy te beschermen. " +
                            "Dit privacybeleid legt uit hoe wij uw persoonsgegevens verzamelen, gebruiken, " +
                            "bekendmaken en beveiligen wanneer u onze diensten gebruikt."
                )
            }

            // Section 1 – Toepassingsgebied
            PolicySection(
                breakpoint = breakpoint,
                title = "1. Toepassingsgebied",
                body = "Dit privacybeleid is van toepassing op:\n" +
                        "• Gebruikers die bedrijven zoeken en recensies schrijven\n" +
                        "• Bedrijfseigenaars en -managers\n" +
                        "• Platformbeheerders\n" +
                        "• Alle gegevens verzameld via klantenstop.be, staging- en ontwikkelomgevingen en gerelateerde platformen"
            )

            // Section 2 – Welke informatie verzamelen we
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(900.px),
                verticalArrangement = Arrangement.spacedBy(20.px)
            ) {
                H2Heading(
                    typography = Typography.TitleLarge,
                    text = "2. Welke informatie verzamelen we",
                    color = MaterialColorVars.Primary.value()
                )

                PolicyCard(breakpoint, "2.1 Informatie die u rechtstreeks verstrekt") {
                    PolicyBulletGroup(
                        breakpoint, "Accountregistratie",
                        listOf("E-mailadres", "Wachtwoord (versleuteld)", "Volledige naam")
                    )
                    PolicyBulletGroup(
                        breakpoint, "Bedrijfsinformatie",
                        listOf(
                            "Bedrijfsnaam, adres en contactgegevens",
                            "Bedrijfsregistratiegegevens",
                            "Categorie / branche",
                            "Website-URL",
                            "Bedrijfsbeschrijving en afbeeldingen"
                        )
                    )
                    PolicyBulletGroup(
                        breakpoint, "Recensies en beoordelingen",
                        listOf(
                            "Schriftelijke recensies en beoordelingen",
                            "Datum en tijdstempel van de recensie",
                            "Bedrijfsinformatie gekoppeld aan de recensie",
                            "Identificatie van de recensent"
                        )
                    )
                }

                PolicyCard(breakpoint, "2.2 Automatisch verzamelde informatie") {
                    PolicyBulletGroup(
                        breakpoint, "Geolocatiegegevens",
                        listOf(
                            "GPS-coördinaten (wanneer u locatieservices inschakelt)",
                            "Locatie op basis van IP-adres",
                            "Zoekstraalsvoorkeuren"
                        )
                    )
                    PolicyBulletGroup(
                        breakpoint, "Apparaat- en browserinformatie",
                        listOf(
                            "Apparaattype, besturingssysteem en versie",
                            "Browsertype en versie",
                            "Apparaatidentificatoren (User-Agent)"
                        )
                    )
                    PolicyBulletGroup(
                        breakpoint, "Cookies en trackingtechnologieën",
                        listOf(
                            "Authenticatiecookies: HTTP-only cookies voor sessiebeheer",
                            "Voorkeuren-cookies: kleurmodus opgeslagen in localStorage (klantenstop:colorMode)",
                            "PostHog Analytics: productanalyses en feature flags voor platformverbetering",
                            "Klaro: cookietoestemmingsbeheer om gebruikersvoorkeuren te respecteren"
                        )
                    )
                }
            }

            // Section 3 – Hoe we uw informatie gebruiken
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(900.px),
                verticalArrangement = Arrangement.spacedBy(20.px)
            ) {
                H2Heading(
                    typography = Typography.TitleLarge,
                    text = "3. Hoe we uw informatie gebruiken",
                    color = MaterialColorVars.Primary.value()
                )

                val usageSections = listOf(
                    "Dienstverlening" to "Aanmaken en beheren van uw account, verwerken van bedrijfsregistraties, " +
                            "weergeven van zoekresultaten op basis van locatie en voorkeuren, en publiceren van recensies en beoordelingen.",
                    "Communicatie" to "Verzenden van accountgerelateerde meldingen (aanmelden, wachtwoord resetten), beantwoorden van " +
                            "vragen en ondersteuningsverzoeken, en informeren over beleidswijzigingen.",
                    "Analyse en verbetering" to "Analyseren van gebruikspatronen om de gebruikerservaring te verbeteren, " +
                            "testen van nieuwe functies, genereren van geaggregeerde anonieme statistieken en optimaliseren van de platformprestaties.",
                    "Beveiliging en fraudepreventie" to "Voorkomen van frauduleuze activiteiten en misbruik, beschermen tegen ongeautoriseerde toegang " +
                            "en handhaven van onze Gebruiksvoorwaarden.",
                    "Personalisatie" to "Aanpassen van uw ervaring op basis van voorkeuren, onthouden van uw kleurmodus (licht/donker) " +
                            "en afstemmen van inhoud en zoekresultaten.",
                    "Wettelijke naleving" to "Voldoen aan wettelijke verplichtingen, reageren op rechtmatige verzoeken van autoriteiten " +
                            "en beschermen van onze juridische rechten."
                )

                usageSections.forEach { (title, body) ->
                    PolicyCard(breakpoint, title) {
                        SpanText(
                            modifier = Modifier
                                .fillMaxWidth()
                                .fontSize(if (breakpoint >= Breakpoint.MD) 15.px else 14.px)
                                .lineHeight(if (breakpoint >= Breakpoint.MD) 24.px else 21.px)
                                .color(MaterialColorVars.OnSurface.value()),
                            text = body
                        )
                    }
                }
            }

            // Section 4 – Rechtsgrondslag (GDPR)
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(900.px),
                verticalArrangement = Arrangement.spacedBy(16.px)
            ) {
                H2Heading(
                    typography = Typography.TitleLarge,
                    text = "4. Rechtsgrondslag (AVG/GDPR)",
                    color = MaterialColorVars.Primary.value()
                )
                SpanText(
                    modifier = Modifier
                        .fillMaxWidth()
                        .fontSize(if (breakpoint >= Breakpoint.MD) 15.px else 14.px)
                        .lineHeight(24.px)
                        .color(MaterialColorVars.OnSurface.value()),
                    text = "Onder de AVG verwerken wij persoonsgegevens op de volgende rechtsgronden:"
                )

                val legalBases = listOf(
                    "Accountregistratiegegevens" to "Contractuele noodzaak",
                    "Bedrijfsinformatie" to "Gerechtvaardigd belang",
                    "Recensies en beoordelingen" to "Contractuele noodzaak & gerechtvaardigd belang",
                    "Locatiegegevens" to "Toestemming",
                    "Analysegegevens" to "Gerechtvaardigd belang",
                    "Beveiligingsmonitoring" to "Gerechtvaardigd belang"
                )

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .borderRadius(12.px)
                        .backgroundColor(MaterialColorVars.SurfaceContainer.value())
                        .padding(0.px),
                    verticalArrangement = Arrangement.spacedBy(0.px)
                ) {
                    // Header row
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .backgroundColor(MaterialColorVars.PrimaryContainer.value())
                            .borderRadius(topLeft = 12.px, topRight = 12.px, bottomLeft = 0.px, bottomRight = 0.px)
                            .padding(leftRight = 20.px, topBottom = 12.px),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        SpanText(
                            modifier = Modifier
                                .fontSize(13.px)
                                .fontWeight(FontWeight.Bold)
                                .color(MaterialColorVars.OnPrimaryContainer.value()),
                            text = "Gegevenstype"
                        )
                        SpanText(
                            modifier = Modifier
                                .fontSize(13.px)
                                .fontWeight(FontWeight.Bold)
                                .color(MaterialColorVars.OnPrimaryContainer.value()),
                            text = "Rechtsgrondslag"
                        )
                    }
                    legalBases.forEach { (dataType, basis) ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(leftRight = 20.px, topBottom = 10.px),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            SpanText(
                                modifier = Modifier
                                    .fontSize(14.px)
                                    .color(MaterialColorVars.OnSurface.value()),
                                text = dataType
                            )
                            SpanText(
                                modifier = Modifier
                                    .fontSize(14.px)
                                    .color(MaterialColorVars.OnSurfaceVariant.value()),
                                text = basis
                            )
                        }
                    }
                }
            }

            // Section 5 – Hoe we uw informatie delen
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(900.px),
                verticalArrangement = Arrangement.spacedBy(16.px)
            ) {
                H2Heading(
                    typography = Typography.TitleLarge,
                    text = "5. Hoe we uw informatie delen",
                    color = MaterialColorVars.Primary.value()
                )

                PolicyCard(breakpoint, "Openbaar gedeelde informatie") {
                    SpanText(
                        modifier = Modifier
                            .fillMaxWidth()
                            .fontSize(if (breakpoint >= Breakpoint.MD) 15.px else 14.px)
                            .lineHeight(24.px)
                            .color(MaterialColorVars.OnSurface.value()),
                        text = "Uw gebruikersnaam, profielfoto en recensies zijn zichtbaar voor alle platformgebruikers. " +
                                "Bedrijfsgegevens zijn openbaar doorzoekbaar."
                    )
                }
                PolicyCard(breakpoint, "Serviceproviders") {
                    SpanText(
                        modifier = Modifier
                            .fillMaxWidth()
                            .fontSize(if (breakpoint >= Breakpoint.MD) 15.px else 14.px)
                            .lineHeight(24.px)
                            .color(MaterialColorVars.OnSurface.value()),
                        text = "We delen informatie met de volgende externe serviceproviders:\n\n" +
                                "• PostHog — productanalyses, gebruiksregistratie en feature flags\n" +
                                "• Klaro — beheer van cookietoestemming en voorkeuren\n" +
                                "• Resend — e-mailbezorging voor wachtwoordresets en accountmeldingen\n" +
                                "• Leaflet.js / OpenStreetMap — interactieve kaarten en geolocatie-functies\n" +
                                "• Hostinginfrastructuur — cloudhosting voor platformlevering\n\n" +
                                "Alle serviceproviders zijn contractueel verplicht uw gegevens te beschermen."
                    )
                }
                PolicyCard(breakpoint, "Wettelijke verplichtingen") {
                    SpanText(
                        modifier = Modifier
                            .fillMaxWidth()
                            .fontSize(if (breakpoint >= Breakpoint.MD) 15.px else 14.px)
                            .lineHeight(24.px)
                            .color(MaterialColorVars.OnSurface.value()),
                        text = "We kunnen informatie bekendmaken wanneer dit wettelijk vereist is, op bevel van de rechtbank, " +
                                "om fraude te voorkomen of om de veiligheid en rechten van onze gebruikers te beschermen."
                    )
                }
            }

            // Section 6 – Gegevensbeveiliging
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(900.px),
                verticalArrangement = Arrangement.spacedBy(16.px)
            ) {
                H2Heading(
                    typography = Typography.TitleLarge,
                    text = "6. Gegevensbeveiliging",
                    color = MaterialColorVars.Primary.value()
                )
                PolicyCard(breakpoint, "Beveiligingsmaatregelen") {
                    val measures = listOf(
                        "Versleuteling: HTTPS/TLS voor gegevens in transit",
                        "Authenticatie: Veilige wachtwoord-hashing en sessiebeheer",
                        "Toegangscontrole: Op rollen gebaseerde toegang tot gebruikersgegevens",
                        "HTTP-only cookies: Sessietokens opgeslagen in beveiligde, HTTP-only cookies",
                        "Token verversingmechanisme: Automatische tokenverversing met mutex-gebaseerde concurrentiebeveiliging",
                        "Invoervalidatie: Bescherming tegen injectieaanvallen",
                        "Regelmatige monitoring: Systeemsurveillance op verdachte activiteiten"
                    )
                    measures.forEach { measure ->
                        SpanText(
                            modifier = Modifier
                                .fillMaxWidth()
                                .fontSize(if (breakpoint >= Breakpoint.MD) 15.px else 14.px)
                                .lineHeight(24.px)
                                .color(MaterialColorVars.OnSurface.value()),
                            text = "• $measure"
                        )
                    }
                }
            }

            // Section 7 – Gegevensbewaring
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(900.px),
                verticalArrangement = Arrangement.spacedBy(16.px)
            ) {
                H2Heading(
                    typography = Typography.TitleLarge,
                    text = "7. Gegevensbewaring",
                    color = MaterialColorVars.Primary.value()
                )

                val retentionRows = listOf(
                    "Accountinformatie" to "Onbepaalde tijd (op verzoek verwijderbaar)",
                    "Recensies en beoordelingen" to "Onbepaalde tijd (tenzij verwijderd door gebruiker)",
                    "Geolocatiegegevens" to "Alleen sessie (niet permanent opgeslagen)",
                    "PostHog analyse-/loggegevens" to "90 dagen",
                    "Klaro cookievoorkeuren" to "Tot handmatig resetten door gebruiker",
                    "Authenticatietokens" to "Tot 24 uur (met automatische verversing)"
                )

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .borderRadius(12.px)
                        .backgroundColor(MaterialColorVars.SurfaceContainer.value()),
                    verticalArrangement = Arrangement.spacedBy(0.px)
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .backgroundColor(MaterialColorVars.PrimaryContainer.value())
                            .borderRadius(topLeft = 12.px, topRight = 12.px, bottomLeft = 0.px, bottomRight = 0.px)
                            .padding(leftRight = 20.px, topBottom = 12.px),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        SpanText(
                            modifier = Modifier.fontSize(13.px).fontWeight(FontWeight.Bold)
                                .color(MaterialColorVars.OnPrimaryContainer.value()),
                            text = "Gegevenstype"
                        )
                        SpanText(
                            modifier = Modifier.fontSize(13.px).fontWeight(FontWeight.Bold)
                                .color(MaterialColorVars.OnPrimaryContainer.value()),
                            text = "Bewaartermijn"
                        )
                    }
                    retentionRows.forEach { (dataType, period) ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(leftRight = 20.px, topBottom = 10.px),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            SpanText(
                                modifier = Modifier.fontSize(14.px).color(MaterialColorVars.OnSurface.value()),
                                text = dataType
                            )
                            SpanText(
                                modifier = Modifier.fontSize(14.px).color(MaterialColorVars.OnSurfaceVariant.value()),
                                text = period
                            )
                        }
                    }
                }
            }

            // Section 8 – Uw rechten
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(900.px),
                verticalArrangement = Arrangement.spacedBy(20.px)
            ) {
                H2Heading(
                    typography = Typography.TitleLarge,
                    text = "8. Uw rechten",
                    color = MaterialColorVars.Primary.value()
                )
                PolicyCard(breakpoint, "AVG-rechten (EU/EER-gebruikers)") {
                    val rights = listOf(
                        "Recht op inzage: Verzoek om een kopie van uw persoonsgegevens",
                        "Recht op rectificatie: Onjuiste informatie corrigeren",
                        "Recht op verwijdering: Verzoek om verwijdering van uw gegevens (\"recht op vergetelheid\")",
                        "Recht op beperking van de verwerking: Beperken hoe we uw gegevens gebruiken",
                        "Recht op gegevensoverdraagbaarheid: Uw gegevens ontvangen in een gestructureerd formaat",
                        "Recht van bezwaar: Bezwaar maken tegen bepaalde verwerkingsactiviteiten",
                        "Recht om toestemming in te trekken: Toestemming intrekken voor optionele verwerking",
                        "Recht om een klacht in te dienen: Een klacht indienen bij uw lokale gegevensbeschermingsautoriteit"
                    )
                    rights.forEach { right ->
                        SpanText(
                            modifier = Modifier
                                .fillMaxWidth()
                                .fontSize(if (breakpoint >= Breakpoint.MD) 15.px else 14.px)
                                .lineHeight(24.px)
                                .color(MaterialColorVars.OnSurface.value()),
                            text = "• $right"
                        )
                    }
                }
                PolicyCard(breakpoint, "Uw rechten uitoefenen") {
                    MarkDownSpanText(
                        modifier = Modifier
                            .fillMaxWidth()
                            .fontSize(if (breakpoint >= Breakpoint.MD) 15.px else 14.px)
                            .lineHeight(if (breakpoint >= Breakpoint.MD) 26.px else 22.px)
                            .color(MaterialColorVars.OnSurface.value()),
                        text = "Neem contact op met ons privacy-team via info@klantenstop.be om uw rechten uit te oefenen. " +
                                "Wij reageren op geverifieerde verzoeken binnen 30 dagen."
                    )
                }
            }

            // Section 9 – Gegevensbeschermingsautoriteit
            PolicySection(
                breakpoint = breakpoint,
                title = "9. Gegevensbeschermingsautoriteit",
                body = "Als u bezwaar heeft over hoe wij uw persoonsgegevens verwerken, kunt u een klacht indienen bij:\n\n" +
                        "Gegevensbeschermingsautoriteit (GBA)\n" +
                        "Drukpersstraat 35, 1000 Brussel, België\n" +
                        "Website: www.gegevensbeschermingsautoriteit.be"
            )

            // Section 10 – Contactinformatie
            PolicySection(
                breakpoint = breakpoint,
                title = "10. Contactinformatie",
                body = "Voor privacygerelateerde vragen, verzoeken om gegevenstoegang of het uitoefenen van uw rechten:\n\n" +
                        "C.B. Connect I.T. B.V.\n" +
                        "Hanegreefstraat 7, 3940 Eksel, België\n\n" +
                        "E-mail: info@klantenstop.be\n" +
                        "Website: klantenstop.be\n\n" +
                        "Reactietijd: Binnen 30 dagen na geverifieerd verzoek."
            )

            // Section 11 – Beleidswijzigingen
            PolicySection(
                breakpoint = breakpoint,
                title = "11. Beleidswijzigingen",
                body = "Wij kunnen dit privacybeleid bijwerken om wijzigingen in onze praktijken, wettelijke vereisten " +
                        "of technologische ontwikkelingen te weerspiegelen. We informeren u over wezenlijke wijzigingen via " +
                        "e-mailmelding of een prominente kennisgeving op onze website. " +
                        "Uw voortgezet gebruik van KlantenStop na wijzigingen vormt acceptatie van het bijgewerkte privacybeleid."
            )

            // Legal links row
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(900.px)
                    .padding(top = 16.px),
                horizontalArrangement = Arrangement.spacedBy(16.px, Alignment.CenterHorizontally),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    modifier = Modifier
                        .fontSize(13.px)
                        .color(MaterialColorVars.OnSurfaceVariant.value()),
                    text = "Zie ook:"
                )
                Link(
                    path = Navigation.Screen.TermsAndConditions.route,
                    variant = UndecoratedLinkVariant,
                    modifier = Modifier
                        .fontSize(13.px)
                        .color(MaterialColorVars.Primary.value())
                ) {
                    SpanText("Algemene Voorwaarden")
                }
            }
        }
    }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

@Composable
private fun PolicySection(
    breakpoint: Breakpoint,
    title: String,
    body: String
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .maxWidth(900.px),
        verticalArrangement = Arrangement.spacedBy(12.px)
    ) {
        H2Heading(typography = Typography.TitleLarge, text = title, color = MaterialColorVars.Primary.value())
        MarkDownSpanText(
            modifier = Modifier
                .fillMaxWidth()
                .fontSize(if (breakpoint >= Breakpoint.MD) 15.px else 14.px)
                .lineHeight(if (breakpoint >= Breakpoint.MD) 26.px else 22.px)
                .color(MaterialColorVars.OnSurface.value()),
            text = body
        )
    }
}

@Composable
private fun PolicyCard(
    breakpoint: Breakpoint,
    title: String,
    content: @Composable () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(12.px)
            .backgroundColor(MaterialColorVars.SurfaceContainer.value())
            .padding(24.px),
        verticalArrangement = Arrangement.spacedBy(10.px)
    ) {
        SpanText(
            modifier = Modifier
                .fillMaxWidth()
                .fontSize(if (breakpoint >= Breakpoint.MD) 16.px else 15.px)
                .fontWeight(FontWeight.SemiBold)
                .color(MaterialColorVars.OnSurface.value()),
            text = title
        )
        content()
    }
}

@Composable
private fun PolicyBulletGroup(
    breakpoint: Breakpoint,
    groupTitle: String,
    items: List<String>
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.px)
    ) {
        SpanText(
            modifier = Modifier
                .fillMaxWidth()
                .fontSize(if (breakpoint >= Breakpoint.MD) 14.px else 13.px)
                .fontWeight(FontWeight.SemiBold)
                .color(MaterialColorVars.OnSurfaceVariant.value()),
            text = groupTitle
        )
        items.forEach { item ->
            SpanText(
                modifier = Modifier
                    .fillMaxWidth()
                    .fontSize(if (breakpoint >= Breakpoint.MD) 14.px else 13.px)
                    .lineHeight(22.px)
                    .color(MaterialColorVars.OnSurface.value()),
                text = "• $item"
            )
        }
    }
}


